import { clsx } from 'clsx';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import Link from 'next/link';
import { Children, Fragment, type ReactNode } from 'react';

export interface NavItem {
  id: string;
  label: string;
  icon: ReactNode;
  badge?: string | number;
  cta?: boolean;
  href?: string;
  /** Render this section label immediately above the item. */
  sectionLabel?: string;
}

interface Props {
  open: boolean;
  collapsed: boolean;
  active: string;
  navItems: NavItem[];
  user: { name: string; plan: string; quotaPct?: number; avatarUrl?: string | null };
  onNavigate: (id: string) => void;
  onToggleCollapse?: () => void;
  /** Right-aligned slot inside the user widget — e.g. sign-out icon button.
      Hidden when the sidebar is collapsed (icon-mode). */
  userActions?: ReactNode;
  /** When set, the avatar + name area becomes a Link to this path (typically /profile). */
  userHref?: string;
}

function formatPlan(plan: string, quotaPct?: number): string {
  if (quotaPct == null) return plan;
  return `${plan} • ${Math.round(quotaPct)}% quota`;
}

type UserShape = { name: string; plan: string; quotaPct?: number; avatarUrl?: string | null };

function UserAvatar({ user }: { user: UserShape }) {
  if (user.avatarUrl) {
    // Plain <img> because the URL is a short-lived presigned MinIO URL we
    // can't whitelist in next/image's remotePatterns.
    return (
      <img
        src={user.avatarUrl}
        alt={user.name}
        className="w-9 h-9 rounded-full object-cover flex-shrink-0"
      />
    );
  }
  return (
    <div className="w-9 h-9 rounded-full bg-sage-lt text-sage-dk flex items-center justify-center font-bold flex-shrink-0">
      {user.name.charAt(0).toUpperCase()}
    </div>
  );
}

function UserMeta({ user }: { user: UserShape }) {
  return (
    <div className="min-w-0 flex-1 text-left">
      <div className="text-sm font-bold text-cream truncate">{user.name}</div>
      <div className="text-xs text-cream/60 truncate">{formatPlan(user.plan, user.quotaPct)}</div>
    </div>
  );
}

export function Sidebar({
  open,
  collapsed,
  active,
  navItems,
  user,
  onNavigate,
  onToggleCollapse,
  userActions,
  userHref,
}: Props) {
  return (
    <aside
      data-testid="sidebar"
      data-open={open}
      data-collapsed={collapsed}
      className={clsx(
        'flex flex-col bg-gradient-to-b from-[#2d2925] to-[#3D362E] text-cream',
        // Only transform animates (mobile drawer slide). Width is instant so
        // the collapse/expand snaps without a long, reflowing layout shift.
        'h-full flex-shrink-0 transition-transform duration-200 ease-out',
        collapsed ? 'lg:w-[72px]' : 'lg:w-64',
        'w-[280px]',
        'fixed inset-y-0 left-0 z-50 shadow-[4px_0_24px_rgba(0,0,0,.15)] lg:shadow-none',
        'lg:relative lg:inset-auto',
        open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
      )}
    >
      <div
        className={clsx(
          'flex items-center gap-2.5 px-5 py-5',
          collapsed && 'lg:justify-center lg:px-3',
        )}
      >
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sage to-forest flex items-center justify-center flex-shrink-0">
          <span className="font-display font-extrabold text-white text-xl leading-none -mt-0.5">
            L
          </span>
        </div>
        {!collapsed && <span className="font-display font-bold text-lg text-cream">Leva</span>}
        {/* Toggle lives in this row only when expanded; when collapsed it
            drops to its own row below so the brand icon centers cleanly. */}
        {onToggleCollapse && !collapsed && (
          <button
            type="button"
            aria-label="Collapse sidebar"
            onClick={onToggleCollapse}
            className="hidden lg:flex w-7 h-7 rounded-lg items-center justify-center text-cream/60 hover:text-cream hover:bg-white/10 transition-colors ml-auto"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
        )}
      </div>
      {onToggleCollapse && collapsed && (
        <div className="hidden lg:flex justify-center pb-2">
          <button
            type="button"
            aria-label="Expand sidebar"
            onClick={onToggleCollapse}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-cream/60 hover:text-cream hover:bg-white/10 transition-colors"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <nav className="flex-1 flex flex-col py-2 overflow-y-auto">
        {navItems.map((it) => {
          const isActive = active === it.id;
          const showAccent = isActive && !it.cta;
          const className = clsx(
            'relative flex items-center gap-3 px-3.5 py-2.5 mx-2.5 my-0.5 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap',
            it.cta
              ? 'bg-gradient-to-br from-coral to-rose text-white hover:brightness-95'
              : 'text-cream/85 hover:bg-white/[0.06] hover:text-cream',
            !it.cta && isActive && 'bg-coral/30 text-[#FBE5D6]',
            showAccent &&
              "before:content-[''] before:absolute before:left-[-10px] before:top-2 before:bottom-2 before:w-[3px] before:bg-coral before:rounded-r-[3px]",
            collapsed && 'lg:justify-center lg:px-2.5',
          );
          const inner = (
            <>
              <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                {it.icon}
              </span>
              {!collapsed && <span className="flex-1 text-left">{it.label}</span>}
              {!collapsed && it.badge != null && (
                <span className="ml-auto text-[10px] font-bold leading-none bg-rose text-white px-2 py-1 rounded-full">
                  {it.badge}
                </span>
              )}
            </>
          );

          const node = it.href ? (
            <Link
              key={`${it.id}-link`}
              // UrlObject form bypasses Next.js typedRoutes (which our package can't
              // know the routes of); consumers control runtime correctness via the
              // href values they pass in NavItems.
              href={{ pathname: it.href }}
              data-active={isActive ? 'true' : 'false'}
              onClick={() => onNavigate(it.id)}
              className={className}
            >
              {inner}
            </Link>
          ) : (
            <button
              key={`${it.id}-btn`}
              type="button"
              data-active={isActive ? 'true' : 'false'}
              onClick={() => onNavigate(it.id)}
              className={className}
            >
              {inner}
            </button>
          );

          if (it.sectionLabel && !collapsed) {
            return (
              <Fragment key={it.id}>
                <div className="px-6 pt-4 pb-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-cream/50">
                  {it.sectionLabel}
                </div>
                {node}
              </Fragment>
            );
          }
          return <Fragment key={it.id}>{node}</Fragment>;
        })}
      </nav>

      <div
        className={clsx(
          'mt-auto p-4 border-t border-[#3D362E] flex items-center gap-3',
          collapsed && 'lg:justify-center lg:px-2',
        )}
      >
        {/* Avatar + name/plan area — wrapped in a Link when userHref is set so
            the user can click their own profile widget to view the profile page.
            Logout stays as a separate flex sibling so its click doesn't trigger
            the parent Link's navigation. */}
        {userHref ? (
          <Link
            href={{ pathname: userHref }}
            onClick={() => onNavigate('profile')}
            aria-label={`Lihat profil ${user.name}`}
            className={clsx(
              'flex items-center gap-3 min-w-0 rounded-xl transition-colors hover:bg-white/[0.06]',
              !collapsed && 'flex-1 -mx-1 px-1 py-1',
            )}
          >
            <UserAvatar user={user} />
            {!collapsed && <UserMeta user={user} />}
          </Link>
        ) : (
          <>
            <UserAvatar user={user} />
            {!collapsed && <UserMeta user={user} />}
          </>
        )}
        {/* `Children.toArray` auto-generates keys. Without it, React 19's
            owner-based key check fires because `userActions` is a JSX element
            created by AppLayout but rendered here as one of several
            JSX-positional children of this flex container — see the dev
            warning at layout.tsx:110 (`userActions={<LogoutButton />}`). */}
        {!collapsed && Children.toArray(userActions)}
      </div>
    </aside>
  );
}
